// Viewer WebRTC unificado para hosts Windows e Android.
//
// Fluxo de sinalização (role=viewer):
//   Viewer envia: {"role":"viewer"}
//   Servidor envia: {"type":"waiting"} ou {"type":"device_connected"}
//   Viewer recebe: {"type":"offer","sdp":"..."} do device
//   Viewer envia:  {"type":"answer","sdp":"..."}
//   Ambos trocam:  {"type":"ice","candidate":"..."}
//
// Modo relay WS (fallback quando ICE não conecta — NAT simétrico / rede restrita):
//   Após 15s sem Event::Connected, o agente envia frames H.264 como WS binário:
//     Header: [0xFF, 0x56, w_lo, w_hi, h_lo, h_hi] + H.264 Annex B
//   O backend repassa o binário diretamente ao viewer.
//   O viewer detecta o magic, decodifica via WebCodecs VideoDecoder e exibe em <canvas>.
//   Eventos de input são enviados via WS: {"type":"input","data":{...}}
//
// Controle remoto (Windows):
//   WebRTC: eventos via RTCDataChannel "input"
//   Relay : eventos via WS JSON {"type":"input","data":{...}}

import { useEffect, useRef, useState, useCallback } from 'react'
import { Monitor, Loader2, X, Smartphone, Wifi } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getWebRtcSignalWsUrl } from '../config/api'
import { managedDevicesApi } from '../services/managedDevicesApi'

// Magic bytes que identificam frame relay no WS binário
const RELAY_MAGIC_0 = 0xFF
const RELAY_MAGIC_1 = 0x56

export default function WebRtcViewer({ hostId, host, onClose }) {
  const { t } = useTranslation()
  const videoRef = useRef(null)
  const canvasRef = useRef(null)           // canvas para modo relay WebCodecs
  const wsRef = useRef(null)
  const pcRef = useRef(null)
  const dcRef = useRef(null)             // data channel para input (WebRTC)
  const decoderRef = useRef(null)        // VideoDecoder (modo relay)
  const cancelledRef = useRef(false)
  const reconnectTimerRef = useRef(null)
  const iceServersRef = useRef([])
  const relayPolicyRef = useRef(false)   // true quando renegociando com iceTransportPolicy: 'relay'
  const relayModeRef = useRef(false)     // true quando usando relay WS (frames binários)
  const decoderInitRef = useRef(false)   // decoder já configurado para dimensões atuais
  const lastRelayDimRef = useRef({ w: 0, h: 0 })
  const streamingStartedRef = useRef(false)  // mirror de `streaming` para evitar dep cycle no useEffect

  const [status, setStatus] = useState(() => t('webrtc.connecting'))
  const [streaming, setStreaming] = useState(false)
  const [isRelayMode, setIsRelayMode] = useState(false)

  const isWindows = host?.hostKind !== 'Android'

  // Busca credenciais ICE (STUN + TURN) do backend ao montar. Sem fallback público:
  // se o backend não devolver iceServers, ICE não tem caminho válido em NAT simétrico.
  useEffect(() => {
    if (!hostId) return
    managedDevicesApi.getWebRtcCredentials(hostId)
      .then((data) => {
        if (data?.iceServers?.length) {
          iceServersRef.current = data.iceServers
        } else {
          console.warn('[webrtc] backend retornou iceServers vazio — coturn provavelmente não está configurado')
        }
      })
      .catch((e) => {
        console.error('[webrtc] falha ao obter iceServers:', e)
      })
  }, [hostId])

  // ── Limpeza ─────────────────────────────────────────────────────────────────

  const cleanup = useCallback(() => {
    clearTimeout(reconnectTimerRef.current)

    if (decoderRef.current) {
      try { decoderRef.current.close() } catch (_) {}
      decoderRef.current = null
    }
    decoderInitRef.current = false
    relayModeRef.current = false
    relayPolicyRef.current = false
    streamingStartedRef.current = false

    if (pcRef.current) {
      pcRef.current.ontrack = null
      pcRef.current.onicecandidate = null
      pcRef.current.oniceconnectionstatechange = null
      try { pcRef.current.close() } catch (_) {}
      pcRef.current = null
    }
    dcRef.current = null

    if (wsRef.current) {
      wsRef.current.onmessage = null
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      const ws = wsRef.current
      wsRef.current = null
      try { ws.close() } catch (_) {}
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setStreaming(false)
    setIsRelayMode(false)
  }, [])

  // ── Signaling helpers ────────────────────────────────────────────────────────

  const sendSignal = useCallback((msg) => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg))
    }
  }, [])

  // ── Relay WebCodecs ──────────────────────────────────────────────────────────

  const initDecoder = useCallback((w, h) => {
    if (decoderRef.current) {
      try { decoderRef.current.close() } catch (_) {}
    }
    decoderInitRef.current = false

    const canvas = canvasRef.current
    if (!canvas) return

    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')

    const decoder = new VideoDecoder({
      output: (frame) => {
        ctx.drawImage(frame, 0, 0)
        frame.close()
      },
      error: (e) => {
        console.warn('[relay] VideoDecoder error:', e)
      },
    })

    decoder.configure({
      codec: 'avc1.42E01E',  // H.264 Baseline
      codedWidth: w,
      codedHeight: h,
      optimizeForLatency: true,
    })

    decoderRef.current = decoder
    decoderInitRef.current = true
    lastRelayDimRef.current = { w, h }
  }, [])

  const handleRelayFrame = useCallback((arrayBuffer) => {
    const bytes = new Uint8Array(arrayBuffer)

    // Valida magic header [0xFF, 0x56, w_lo, w_hi, h_lo, h_hi]
    if (bytes.length < 6 || bytes[0] !== RELAY_MAGIC_0 || bytes[1] !== RELAY_MAGIC_1) return

    const w = bytes[2] | (bytes[3] << 8)
    const h = bytes[4] | (bytes[5] << 8)
    if (w === 0 || h === 0) return

    const h264 = bytes.slice(6)
    if (h264.length === 0) return

    // Recria decoder se as dimensões mudaram (ex: resolução alterada no servidor)
    const dim = lastRelayDimRef.current
    if (!decoderInitRef.current || dim.w !== w || dim.h !== h) {
      initDecoder(w, h)
    }

    const decoder = decoderRef.current
    if (!decoder || decoder.state === 'closed') return

    // Detecta se é keyframe: NAL type 5 (IDR) no primeiro NAL unit
    // Annex B: 00 00 00 01 [nal_unit_type = byte & 0x1F]
    let isKey = false
    for (let i = 0; i < h264.length - 4; i++) {
      if (h264[i] === 0 && h264[i+1] === 0 && h264[i+2] === 0 && h264[i+3] === 1) {
        const nalType = h264[i+4] & 0x1F
        if (nalType === 5 || nalType === 7 || nalType === 8) { isKey = true; break }
      }
    }

    try {
      const chunk = new EncodedVideoChunk({
        type: isKey ? 'key' : 'delta',
        timestamp: performance.now() * 1000,
        data: h264,
      })
      decoder.decode(chunk)
    } catch (e) {
      console.warn('[relay] decode error:', e)
    }

    // Ativa streaming no primeiro frame (uma única vez por sessão de WS)
    if (!streamingStartedRef.current) {
      streamingStartedRef.current = true
      setStreaming(true)
      setIsRelayMode(true)
      setStatus(t('webrtc.streamingRelay'))
    }
  }, [initDecoder, t])

  // ── WebRTC answer/ICE ────────────────────────────────────────────────────────

  const startPeerConnection = useCallback(async (offerSdp) => {
    if (cancelledRef.current) return

    if (pcRef.current) {
      try { pcRef.current.close() } catch (_) {}
      pcRef.current = null
    }

    // Primeira tentativa: 'all' (host + srflx + relay). Se falhar, renegocia com 'relay'.
    const config = {
      iceServers: iceServersRef.current,
      iceTransportPolicy: relayPolicyRef.current ? 'relay' : 'all',
    }
    const pc = new RTCPeerConnection(config)
    pcRef.current = pc

    // Recebe o track de vídeo do device
    pc.ontrack = (e) => {
      if (cancelledRef.current || !videoRef.current) return
      if (videoRef.current.srcObject !== e.streams[0]) {
        videoRef.current.srcObject = e.streams[0]
        videoRef.current.play().catch(() => {})
      }
    }

    // Envia ICE candidates locais via signaling
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        sendSignal({ type: 'ice', candidate: e.candidate.candidate })
      }
    }

    pc.oniceconnectionstatechange = async () => {
      const s = pc.iceConnectionState
      if (s === 'connected' || s === 'completed') {
        setStatus(t('webrtc.streaming'))
        setStreaming(true)
        relayModeRef.current = false
        setIsRelayMode(false)
      } else if (s === 'failed') {
        setStatus(t('webrtc.iceFailed'))
        setStreaming(false)
        // Se ainda não tentamos relay-only, marca o flag e força reconexão limpa do WS:
        // o ws.onclose reagenda connect() em 3s, e o device reenvia offer; o próximo
        // RTCPeerConnection sairá com iceTransportPolicy='relay' (ignora host/srflx).
        // Caso contrário, aguarda o relay-WS binário do agente (Windows).
        if (!relayPolicyRef.current && iceServersRef.current.length > 0) {
          relayPolicyRef.current = true
          console.warn('[webrtc] ICE falhou em modo "all" — reconectando com iceTransportPolicy: "relay"')
          try { wsRef.current?.close() } catch (_) {}
        }
      } else if (s === 'disconnected') {
        setStatus(t('webrtc.unstable'))
        setStreaming(false)
      }
    }

    // Data channel de input aberto pelo device (para hosts Windows)
    pc.ondatachannel = (e) => {
      if (e.channel.label === 'input') {
        dcRef.current = e.channel
      }
    }

    // Processa offer e gera answer
    await pc.setRemoteDescription({ type: 'offer', sdp: offerSdp })
    const answer = await pc.createAnswer()
    await pc.setLocalDescription(answer)
    sendSignal({ type: 'answer', sdp: answer.sdp })
  }, [sendSignal])

  // ── Conexão ao signaling ─────────────────────────────────────────────────────

  const connect = useCallback(() => {
    if (cancelledRef.current) return

    let url
    try {
      url = getWebRtcSignalWsUrl(hostId)
    } catch (e) {
      setStatus(e.message || t('webrtc.sessionError'))
      return
    }

    setStatus(t('webrtc.connecting'))
    setStreaming(false)

    const ws = new WebSocket(url)
    ws.binaryType = 'arraybuffer'   // necessário para receber frames relay binários
    wsRef.current = ws

    ws.onopen = () => {
      if (cancelledRef.current) { ws.close(); return }
      ws.send(JSON.stringify({ role: 'viewer' }))
    }

    ws.onmessage = async (e) => {
      if (cancelledRef.current) return

      // Mensagem binária = frame relay H.264
      if (e.data instanceof ArrayBuffer) {
        handleRelayFrame(e.data)
        return
      }

      let msg
      try { msg = JSON.parse(e.data) } catch { return }

      switch (msg.type) {
        case 'waiting':
          setStatus(t('webrtc.waitingDevice'))
          break

        case 'device_connected':
          setStatus(t('webrtc.deviceConnected'))
          break

        case 'offer':
          if (msg.sdp) {
            setStatus(t('webrtc.establishing'))
            await startPeerConnection(msg.sdp)
          }
          break

        case 'answer':
          // Não esperado no lado viewer, mas defensivo
          break

        case 'ice': {
          const pc = pcRef.current
          if (pc && msg.candidate) {
            try {
              await pc.addIceCandidate(new RTCIceCandidate({ candidate: msg.candidate, sdpMid: '0', sdpMLineIndex: 0 }))
            } catch (_) {}
          }
          break
        }

        case 'device_disconnected':
          setStatus(t('webrtc.deviceDisconnected'))
          setStreaming(false)
          relayModeRef.current = false
          setIsRelayMode(false)
          if (pcRef.current) {
            try { pcRef.current.close() } catch (_) {}
            pcRef.current = null
          }
          if (decoderRef.current) {
            try { decoderRef.current.close() } catch (_) {}
            decoderRef.current = null
            decoderInitRef.current = false
          }
          break
      }
    }

    ws.onclose = () => {
      if (cancelledRef.current) return
      setStatus(t('webrtc.disconnected'))
      setStreaming(false)
      reconnectTimerRef.current = setTimeout(() => {
        if (!cancelledRef.current) connect()
      }, 3000)
    }

    ws.onerror = () => {
      setStatus(t('webrtc.serverError'))
    }
  }, [hostId, startPeerConnection, handleRelayFrame])

  useEffect(() => {
    cancelledRef.current = false
    connect()
    return () => {
      cancelledRef.current = true
      cleanup()
    }
  }, [connect, cleanup])

  // ── Controle remoto: mouse (Windows) ─────────────────────────────────────────

  const sendInput = useCallback((msg) => {
    // Modo relay: envia via WS signaling (sem DataChannel)
    if (relayModeRef.current || isRelayMode) {
      sendSignal({ type: 'input', data: msg })
      return
    }
    // Modo WebRTC: envia via DataChannel
    const dc = dcRef.current
    if (dc && dc.readyState === 'open') {
      dc.send(JSON.stringify(msg))
    }
  }, [isRelayMode, sendSignal])

  const getVideoSize = useCallback((el) => {
    if (isRelayMode) {
      return { vw: lastRelayDimRef.current.w || el.offsetWidth, vh: lastRelayDimRef.current.h || el.offsetHeight }
    }
    return { vw: el.videoWidth || el.offsetWidth, vh: el.videoHeight || el.offsetHeight }
  }, [isRelayMode])

  const handleMouseMove = useCallback((e) => {
    if (!isWindows || !streaming) return
    const rect = e.currentTarget.getBoundingClientRect()
    const { vw, vh } = getVideoSize(e.currentTarget)
    sendInput({
      type: 'mouse',
      x: Math.round((e.clientX - rect.left) * (vw / rect.width)),
      y: Math.round((e.clientY - rect.top) * (vh / rect.height)),
      buttons: e.buttons,
    })
  }, [isWindows, streaming, sendInput, getVideoSize])

  const handleMouseDown = useCallback((e) => {
    if (!isWindows || !streaming) return
    const rect = e.currentTarget.getBoundingClientRect()
    const { vw, vh } = getVideoSize(e.currentTarget)
    sendInput({
      type: 'mouse',
      x: Math.round((e.clientX - rect.left) * (vw / rect.width)),
      y: Math.round((e.clientY - rect.top) * (vh / rect.height)),
      buttons: e.buttons,
    })
  }, [isWindows, streaming, sendInput, getVideoSize])

  const handleMouseUp = useCallback((e) => {
    if (!isWindows || !streaming) return
    const rect = e.currentTarget.getBoundingClientRect()
    const { vw, vh } = getVideoSize(e.currentTarget)
    sendInput({
      type: 'mouse',
      x: Math.round((e.clientX - rect.left) * (vw / rect.width)),
      y: Math.round((e.clientY - rect.top) * (vh / rect.height)),
      buttons: 0,
    })
  }, [isWindows, streaming, sendInput, getVideoSize])

  const handleKeyDown = useCallback((e) => {
    if (!isWindows || !streaming) return
    e.preventDefault()
    sendInput({ type: 'key', code: e.keyCode, down: true })
  }, [isWindows, streaming, sendInput])

  const handleKeyUp = useCallback((e) => {
    if (!isWindows || !streaming) return
    sendInput({ type: 'key', code: e.keyCode, down: false })
  }, [isWindows, streaming, sendInput])

  const inputHandlers = isWindows ? {
    onMouseMove: handleMouseMove,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    tabIndex: 0,
    style: { cursor: streaming ? 'none' : 'default', outline: 'none', width: '100%', height: '100%', objectFit: 'contain' },
  } : {
    tabIndex: -1,
    style: { cursor: 'default', outline: 'none', width: '100%', height: '100%', objectFit: 'contain' },
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-[100] bg-black overflow-hidden flex flex-col items-center justify-center">
      {/* Overlay de status (aparece enquanto não está streamando) */}
      {!streaming && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10 bg-black/70">
          {host?.hostKind === 'Android'
            ? <Smartphone className="w-10 h-10 text-gray-500" />
            : <Monitor className="w-10 h-10 text-gray-500" />
          }
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            <span className="text-sm text-gray-300">{status}</span>
          </div>
        </div>
      )}

      {/* Indicador de status em canto quando streamando */}
      {streaming && (
        <div className="absolute top-2 left-2 z-20 flex items-center gap-1 bg-black/60 rounded px-2 py-1 text-[11px]">
          <Wifi className="w-3 h-3 text-green-400" />
          <span className={isRelayMode ? 'text-yellow-400' : 'text-green-400'}>
            {isRelayMode ? t('webrtc.relay') : t('webrtc.live')}
          </span>
        </div>
      )}

      {/* Botão fechar */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-2 right-2 z-20 p-2 rounded-md bg-black/50 hover:bg-black/80 text-gray-400 hover:text-white transition-colors"
        title={t('webrtc.close')}
      >
        <X className="w-5 h-5" />
      </button>

      {/* Vídeo WebRTC (modo normal) */}
      {/* Recebe teclado/mouse do controle remoto: elemento nao-interativo com handlers de
          input de proposito (nao ha equivalente interativo para um framebuffer). */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        {...inputHandlers}
        className={isRelayMode ? 'hidden' : 'w-full h-full object-contain'}
      />

      {/* Canvas WebCodecs (modo relay) */}
      {/* Recebe teclado/mouse do controle remoto: elemento nao-interativo com handlers de
          input de proposito (nao ha equivalente interativo para um framebuffer). */}
      <canvas
        ref={canvasRef}
        {...inputHandlers}
        className={isRelayMode ? 'w-full h-full object-contain' : 'hidden'}
      />
    </div>
  )
}
